import { useEffect, useId, useRef, useState } from 'react'
import { CompanionEvidence } from '@/components/CompanionEvidence'
import { validAggregate, type AggregateEvidence } from '@/lib/aggregate'

export interface InsightWithEvidenceProps { number: number; unit: string; label: string; evidence: AggregateEvidence }
export function InsightWithEvidence(props: InsightWithEvidenceProps) {
  const [open, setOpen] = useState(false)
  // Defense at the render boundary, including JavaScript callers and stale servers.
  if (!validAggregate({ status: 'known', ...props })) return null
  return <div className="rounded-r-sm border border-divider-subtle bg-bg-elevated p-3">
    <span>{props.number} {props.unit} {props.label}</span>
    <button type="button" aria-label={`Evidence for ${props.unit} ${props.label}`} onClick={() => setOpen(true)}
      className="ml-2 inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-divider-subtle">ⓘ</button>
    <span className="block text-caption text-text-secondary">Record-backed · limited confidence{props.evidence.missing_ranges.length > 0 ? ' · coverage unknown' : ''}</span>
    {open && <InsightEvidenceModal evidence={props.evidence} onClose={() => setOpen(false)} />}
  </div>
}
// Native dialog follows WhyTapModal: focus trap, Escape, inert backdrop and focus restoration.
export function InsightEvidenceModal({ evidence, onClose }: { evidence: AggregateEvidence; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  useEffect(() => { const node = dialog.current; node?.showModal(); return () => node?.close() }, [])
  return <dialog ref={dialog} aria-labelledby={titleId} onCancel={onClose}
    onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    className="w-[calc(100%-2rem)] max-w-lg max-h-[85dvh] overflow-y-auto rounded-r-sm bg-bg-elevated text-text-primary border border-divider-subtle p-0 backdrop:bg-black/60">
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 id={titleId} className="text-title">Why this total?</h2>
        <button type="button" autoFocus onClick={onClose} className="min-h-11 px-3 underline">Close</button>
      </div>
      <CompanionEvidence evidence={evidence} />
      <p className="text-caption text-text-secondary">Recorded observations, not medical advice or a statement that care is safe. Sleep durations include only the part inside this interval; a boundary start may precede it.</p>
    </div>
  </dialog>
}
